import {
  FileAddOutlined,
  InfoCircleOutlined,
  CopyOutlined,
} from '@ant-design/icons';
import ReactQuill from 'react-quill';
import {
  AButtonPrint,
  AContainerFooter,
  AInput,
  ASelect,
  ContainerForm,
  Loading,
  OptionFooter,
  PageIntro,
  PageLayout,
  PagePanel,
} from '../../../components';
import { useNavigate, useParams } from 'react-router-dom';
import { useToast } from '../../../hooks/toast';
import { useCallback, useEffect, useState } from 'react';
import { Col, Form, Row, Tooltip, Space, Button } from 'antd';
import {
  IClient,
  IHealthCertificate,
  IModelHealthCertificate,
  IProfessional,
} from '../../../shared/interfaces';
import api from '../../../services/api';
import {
  formatsText,
  modulesText,
} from '../../../shared/utils/propreties-text';
import { schemaClientHealthCertificate } from '../schemas/yup.client-health-certificate.schema';
import { formatDate } from '../../../shared/utils/format-date';
import * as S from './styles';
import { useI18n } from '../../../shared/i18n';

const HealthCertificateCreateEdit: React.FC = () => {
  const { id } = useParams();
  const { addToast } = useToast();
  const navigate = useNavigate();
  const { t } = useI18n();
  const [professionals, setProfessionals] = useState<IProfessional[]>([]);
  const [clients, setClients] = useState<IClient[]>([]);
  const [professionalId, setProfessionalId] = useState<string>();
  const [clientId, setClientId] = useState<string>();
  const [startTime, setStartTime] = useState<string>();
  const [endTime, setEndTime] = useState<string>();
  const [date, setDate] = useState<string>();
  const [qtDays, setQtDays] = useState<number>(0);
  const [cid, setCid] = useState<string>();
  const [modelHealthCertificateId, setModelHealthCertificateId] =
    useState<string>();
  const [modelsHealthCertificates, setModelsHealthCertificates] = useState<
    IModelHealthCertificate[]
  >([]);
  const [text, setText] = useState<string>();
  const [loading, setLoading] = useState<boolean>(true);
  const [showButtonPrint, setShowButtonPrint] = useState<boolean>(false);

  const loadProfessionals = useCallback(async () => {
    try {
      const response = await api.get(`/professionals/select`);
      setProfessionals(response.data);
    } catch (err) {
      addToast({
        type: 'error',
        title: t('healthCertificateCreateEdit.toast.errorTitle'),
        description: t(
          'healthCertificateCreateEdit.toast.listProfessionalsError',
        ),
      });
    }
  }, [addToast, t]);

  const loadModelsHealthCertificates = useCallback(async () => {
    try {
      const response = await api.get(`/health-certificates/select`);
      setModelsHealthCertificates(response.data);
    } catch (err) {
      addToast({
        type: 'error',
        title: t('healthCertificateCreateEdit.toast.errorTitle'),
        description: t('healthCertificateCreateEdit.toast.listModelsError'),
      });
    } finally {
      setLoading(false);
    }
  }, [addToast, t]);

  const loadClients = useCallback(async () => {
    await api
      .get(`/clients/select/${1}`)
      .then((response) => {
        setClients(response.data);
      })
      .catch((error) => {
        addToast({
          type: 'error',
          title: t('healthCertificateCreateEdit.toast.errorTitle'),
          description: `${error.response.data.message}`,
        });
      });
  }, [addToast, t]);

  const loadData = useCallback(async () => {
    interface IResponseClientHealthCertificate {
      data: IHealthCertificate;
    }

    await api.get(`/clients-health-certificates/${id}`).then((response) => {
      const { data }: IResponseClientHealthCertificate = response;
      setStartTime(data.start_time);
      setEndTime(data.end_time);
      setDate(formatDate(data.date, 'YYYY-MM-DD'));
      setQtDays(+data.quantity_days);
      setCid(data.cid);
      setModelHealthCertificateId(data.health_certificate_id);
      setClientId(data.client_id);
      setProfessionalId(data.professional_id);
      setText(data.text_description);
      setShowButtonPrint(true);
    });
  }, []);

  const handleCreateSubmit = useCallback(async () => {
    setLoading(true);
    schemaClientHealthCertificate
      .validate({
        start_time: startTime,
        end_time: endTime,
        date,
        client_id: clientId,
        professional_id: professionalId,
        health_certificate_id: modelHealthCertificateId,
        quantity_days: qtDays,
        cid,
        text_description: text,
      })
      .then(async (validated) => {
        await api
          .post(`/clients-health-certificates/`, validated)
          .then(() => {
            addToast({
              type: 'success',
              title: t('healthCertificateCreateEdit.toast.createdTitle'),
              description: t(
                'healthCertificateCreateEdit.toast.createdDescription',
              ),
            });
            setTimeout(function () {
              navigate(-1);
            }, 1100);
          })
          .catch((error) => {
            addToast({
              type: 'error',
              title: t('healthCertificateCreateEdit.toast.errorTitle'),
              description:
                error.response.data.message ??
                t('healthCertificateCreateEdit.toast.createError'),
            });
          });
      })
      .catch((err) => {
        addToast({
          type: 'error',
          title: t('healthCertificateCreateEdit.toast.blankFieldsTitle'),
          description: err.errors[0],
        });
      })
      .finally(() => {
        setLoading(false);
      });
  }, [
    startTime,
    endTime,
    date,
    qtDays,
    clientId,
    cid,
    professionalId,
    modelHealthCertificateId,
    text,
    addToast,
    t,
  ]);

  const handleEditSubmit = useCallback(async () => {
    setLoading(true);
    schemaClientHealthCertificate
      .validate({
        start_time: startTime,
        end_time: endTime,
        date,
        client_id: clientId,
        professional_id: professionalId,
        health_certificate_id: modelHealthCertificateId,
        quantity_days: qtDays,
        cid,
        text_description: text,
      })
      .then(async (validated) => {
        await api
          .patch(`/clients-health-certificates/${id}`, validated)
          .then(() => {
            addToast({
              type: 'info',
              title: t('healthCertificateCreateEdit.toast.updatedTitle'),
              description: t(
                'healthCertificateCreateEdit.toast.updatedDescription',
              ),
            });
          })
          .catch((error) => {
            addToast({
              type: 'error',
              title: t('healthCertificateCreateEdit.toast.errorTitle'),
              description:
                error.response.data.message ??
                t('healthCertificateCreateEdit.toast.editError'),
            });
          });
      })
      .catch((err) => {
        addToast({
          type: 'error',
          title: t('healthCertificateCreateEdit.toast.blankFieldsTitle'),
          description: err.errors[0],
        });
      })
      .finally(() => {
        setLoading(false);
      });
  }, [
    startTime,
    endTime,
    date,
    qtDays,
    cid,
    clientId,
    professionalId,
    modelHealthCertificateId,
    text,
    addToast,
    t,
  ]);

  const handleCopyKeyword = (keyword: string) => {
    navigator.clipboard.writeText(keyword);
    addToast({
      type: 'success',
      title: t('healthCertificateCreateEdit.toast.keywordCopiedTitle'),
      description: t(
        'healthCertificateCreateEdit.toast.keywordCopiedDescription',
        { keyword },
      ),
    });
  };

  const keywords = [
    {
      key: '{nome}',
      description: t('healthCertificateCreateEdit.keywords.patientName'),
    },
    {
      key: '{cpf}',
      description: t('healthCertificateCreateEdit.keywords.patientCpf'),
    },
    {
      key: '{qtdDias}',
      description: t('healthCertificateCreateEdit.keywords.daysCount'),
    },
    {
      key: '{data}',
      description: t('healthCertificateCreateEdit.keywords.appointmentDate'),
    },
    {
      key: '{horaInicial}',
      description: t('healthCertificateCreateEdit.keywords.startTime'),
    },
    {
      key: '{horaFinal}',
      description: t('healthCertificateCreateEdit.keywords.endTime'),
    },
    {
      key: '{cid}',
      description: t('healthCertificateCreateEdit.keywords.cid'),
    },
  ];

  const changeText = (id: string) => {
    const findedText = modelsHealthCertificates.find(
      (x) => x.id === id,
    )?.text_description;
    setText(findedText ?? '');
    setModelHealthCertificateId(id);
  };

  useEffect(() => {
    loadProfessionals();
    loadClients();
    loadModelsHealthCertificates();

    if (id !== 'new') {
      loadData();
    }
  }, [id]);

  if (loading) return <Loading load />;

  return (
    <PageLayout>
      <PageIntro
        badge={t('healthCertificateCreateEdit.intro.badge')}
        icon={<FileAddOutlined />}
        title={
          id === 'new'
            ? t('healthCertificateCreateEdit.intro.createTitle')
            : t('healthCertificateCreateEdit.intro.editTitle')
        }
        description={t('healthCertificateCreateEdit.intro.description')}
      />
      <PagePanel>
        <ContainerForm>
          <Form layout="vertical" style={{ width: '90%', marginTop: 24 }}>
            <S.StyledCard>
              <S.FormSectionTitle>
                <span className="icon">👤</span>
                {t('healthCertificateCreateEdit.sections.selection')}
              </S.FormSectionTitle>

              <S.InstructionBox>
                <InfoCircleOutlined className="icon" />
                <p className="text">
                  {t('healthCertificateCreateEdit.instructions.selection')}
                </p>
              </S.InstructionBox>

              <Row gutter={16}>
                <Col span={8}>
                  <Form.Item
                    label={t('healthCertificateCreateEdit.fields.model')}
                    required
                    tooltip={t('healthCertificateCreateEdit.tooltips.model')}
                  >
                    <ASelect
                      showSearch
                      placeholder={t(
                        'healthCertificateCreateEdit.placeholders.model',
                      )}
                      value={modelHealthCertificateId}
                      onChange={(e) => changeText(e)}
                      filterOption={(input, option: any) =>
                        (option?.label ?? '')
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      allowClear
                      options={modelsHealthCertificates.map((prof) => ({
                        label: prof.title,
                        value: prof.id,
                      }))}
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label={t('healthCertificateCreateEdit.fields.patient')}
                    required
                    tooltip={t('healthCertificateCreateEdit.tooltips.patient')}
                  >
                    <ASelect
                      showSearch
                      placeholder={t(
                        'healthCertificateCreateEdit.placeholders.patient',
                      )}
                      value={clientId}
                      onChange={(e) => setClientId(e)}
                      filterOption={(input, option: any) =>
                        (option?.label ?? '')
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      allowClear
                      options={clients.map((cli) => ({
                        label: cli.corporate_name,
                        value: cli.id,
                      }))}
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    label={t('healthCertificateCreateEdit.fields.professional')}
                    required
                    tooltip={t(
                      'healthCertificateCreateEdit.tooltips.professional',
                    )}
                  >
                    <ASelect
                      showSearch
                      placeholder={t(
                        'healthCertificateCreateEdit.placeholders.professional',
                      )}
                      value={professionalId}
                      onChange={(e) => setProfessionalId(e)}
                      filterOption={(input, option: any) =>
                        (option?.label ?? '')
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      allowClear
                      options={professionals.map((prof) => ({
                        label: prof.name,
                        value: prof.id,
                      }))}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </S.StyledCard>

            {/* Seção de Informações do Atendimento */}
            <S.StyledCard>
              <S.FormSectionTitle>
                <span className="icon">⏰</span>
                {t('healthCertificateCreateEdit.sections.appointmentInfo')}
              </S.FormSectionTitle>

              <S.InstructionBox>
                <InfoCircleOutlined className="icon" />
                <p className="text">
                  {t(
                    'healthCertificateCreateEdit.instructions.appointmentInfo',
                  )}
                </p>
              </S.InstructionBox>

              <Row gutter={16}>
                <Col span={6}>
                  <Form.Item
                    label={t('healthCertificateCreateEdit.fields.startTime')}
                    required
                    tooltip={t(
                      'healthCertificateCreateEdit.tooltips.startTime',
                    )}
                  >
                    <AInput
                      value={startTime}
                      type="time"
                      onChange={(e) => setStartTime(e.target.value)}
                    />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item
                    label={t('healthCertificateCreateEdit.fields.endTime')}
                    required
                    tooltip={t('healthCertificateCreateEdit.tooltips.endTime')}
                  >
                    <AInput
                      value={endTime}
                      type="time"
                      onChange={(e) => setEndTime(e.target.value)}
                    />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item
                    label={t('healthCertificateCreateEdit.fields.date')}
                    required
                    tooltip={t('healthCertificateCreateEdit.tooltips.date')}
                  >
                    <AInput
                      value={date}
                      type="date"
                      onChange={(e) => setDate(e.target.value)}
                    />
                  </Form.Item>
                </Col>
                <Col span={6}>
                  <Form.Item
                    label={t('healthCertificateCreateEdit.fields.quantityDays')}
                    required
                    tooltip={t(
                      'healthCertificateCreateEdit.tooltips.quantityDays',
                    )}
                  >
                    <AInput
                      value={qtDays}
                      type="number"
                      onChange={(e) => setQtDays(+e.target.value)}
                      min={0}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row>
                <Col span={8}>
                  <Form.Item
                    label={t('healthCertificateCreateEdit.fields.cid')}
                    tooltip={t('healthCertificateCreateEdit.tooltips.cid')}
                  >
                    <AInput
                      value={cid}
                      placeholder={t(
                        'healthCertificateCreateEdit.placeholders.cid',
                      )}
                      onChange={(e) => setCid(e.target.value)}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </S.StyledCard>

            {/* Seção de Palavras-chave */}
            <S.StyledCard>
              <S.FormSectionTitle>
                <span className="icon">🔑</span>
                {t('healthCertificateCreateEdit.sections.keywords')}
              </S.FormSectionTitle>

              <S.InstructionBox>
                <InfoCircleOutlined className="icon" />
                <p className="text">
                  {t('healthCertificateCreateEdit.instructions.keywords')}
                </p>
              </S.InstructionBox>

              <Space direction="vertical" style={{ width: '100%' }} size={0}>
                {keywords.map((item, index) => (
                  <S.KeywordItem key={`keyword-${index}`}>
                    <span className="keyword-key">{item.key}</span>
                    <span className="keyword-desc">{item.description}</span>
                    <Tooltip
                      title={t(
                        'healthCertificateCreateEdit.actions.copyKeyword',
                      )}
                    >
                      <Button
                        type="text"
                        size="small"
                        icon={<CopyOutlined />}
                        onClick={() => handleCopyKeyword(item.key)}
                        className="copy-btn"
                      />
                    </Tooltip>
                  </S.KeywordItem>
                ))}
              </Space>

              <div
                style={{
                  marginTop: 16,
                  padding: '12px 12px',
                  backgroundColor: '#fffbe6',
                  borderLeft: '3px solid #faad14',
                  borderRadius: '4px',
                }}
              >
                <strong style={{ color: '#ad6800' }}>
                  {t('healthCertificateCreateEdit.warning.title')}
                </strong>
                <p
                  style={{
                    marginBottom: 0,
                    marginTop: 4,
                    color: '#ad6800',
                    fontSize: 13,
                  }}
                >
                  {t('healthCertificateCreateEdit.warning.descriptionStart')}{' '}
                  <code
                    style={{
                      backgroundColor: '#fff7e6',
                      padding: '2px 4px',
                      borderRadius: '2px',
                    }}
                  >
                    {'{'} {'}'}
                  </code>{' '}
                  {t('healthCertificateCreateEdit.warning.descriptionEnd')}
                </p>
              </div>
            </S.StyledCard>

            {/* Seção de Conteúdo do Atestado */}
            <S.StyledCard>
              <S.FormSectionTitle>
                <span className="icon">📄</span>
                {t('healthCertificateCreateEdit.sections.content')}
              </S.FormSectionTitle>

              <S.InstructionBox>
                <InfoCircleOutlined className="icon" />
                <p className="text">
                  {t('healthCertificateCreateEdit.instructions.content')}
                </p>
              </S.InstructionBox>

              <Row>
                <Col span={24}>
                  <Form.Item
                    label={t('healthCertificateCreateEdit.fields.text')}
                    required
                  >
                    <ReactQuill
                      theme="snow"
                      value={text}
                      onChange={setText}
                      modules={modulesText}
                      formats={formatsText}
                      style={{ height: '35vh', marginBottom: 20 }}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </S.StyledCard>

            <AContainerFooter>
              <OptionFooter
                onClickCancel={() => navigate(-1)}
                onClickOk={id !== 'new' ? handleEditSubmit : handleCreateSubmit}
                cancelText={t('healthCertificateCreateEdit.actions.back')}
                okText={t('common.save')}
              />
              <AButtonPrint
                show={showButtonPrint}
                onClick={() => {
                  navigate(`/health_certificate/${id}/print`);
                }}
              />
            </AContainerFooter>
          </Form>
        </ContainerForm>
      </PagePanel>
    </PageLayout>
  );
};

export default HealthCertificateCreateEdit;
